/**
 * Pushdown Automata (PDA) for tracking game state transitions
 * 
 * Inspired by Garfinkel (2020) on using PDA for managing state transitions in games.
 * 
 * A PDA is a computational model that extends finite automata with a stack data structure.
 * In this game, it tracks the player's move history, demonstrating how computational
 * theory concepts can be applied to game state management.
 * 
 * Educational Purpose:
 * - Demonstrates stack-based state management
 * - Shows how formal computational models apply to real-world problems
 * - Provides visual feedback of the computation process
 */

export class PDA {
  private state: string;
  private stack: string[];

  /**
   * Initialize the PDA with starting state and empty stack
   */
  constructor() {
    this.state = 'start';
    this.stack = [];
  }

  /**
   * Push a symbol onto the stack
   * @param symbol - The symbol to push (typically a move description)
   */
  push(symbol: string): void {
    this.stack.push(symbol);
  }

  /**
   * Pop a symbol from the stack
   * @returns The popped symbol, or undefined if stack is empty
   */
  pop(): string | undefined {
    return this.stack.pop();
  }

  /**
   * Perform a state transition based on input
   * This logs the move on the stack and updates the internal state
   * 
   * @param input - The input symbol (move description)
   */
  transition(input: string): void {
    this.push(input);
    this.state = 'processing';
  }

  /**
   * Get a copy of the current stack
   * Returns a copy to prevent external mutation
   * 
   * @returns Immutable copy of the stack
   */
  getStack(): string[] {
    return [...this.stack];
  }

  /**
   * Get the current state of the PDA
   * @returns Current state string
   */
  getState(): string {
    return this.state;
  }

  /**
   * Get the size of the stack
   * Useful for UI display and debugging
   * 
   * @returns Number of elements in the stack
   */
  getStackSize(): number {
    return this.stack.length;
  }

  /**
   * Check if the stack is empty
   * @returns True if stack has no elements
   */
  isEmpty(): boolean {
    return this.stack.length === 0;
  }

  /**
   * Peek at the top of the stack without removing it
   * @returns The top element, or undefined if stack is empty
   */
  peek(): string | undefined {
    return this.stack[this.stack.length - 1];
  }

  /**
   * Reset the PDA to initial state
   * Useful for starting a new game
   */
  reset(): void {
    this.state = 'start';
    this.stack = [];
  }
}